
import _get from 'lodash/get';
import _set from 'lodash/set';
import PlayerType from '../enums/player-type';
import HTML5Player from './html5/html5-player';
import HLSJSPlayer from './hlsjs/hlsjs-player';

/**
 *
 * @module PlayerFactory
 * @desc Player object creation factory. Creates a concrete player
 *       based on media url and an optional playerType override.
 * @class PlayerFactory
 *
 */

const playerTypes = {
    HTML5: HTML5Player,
    HLSJS: HLSJSPlayer,
};
class PlayerFactory {

    /**
     *
     * @static
     * @desc Creates a concrete implementation of AbstractPlayer based on the provided
     * MediaPlayerConfig object. Currently only creates the HTML5 player.
     * @param {MediaPlayerConfig} config The media player's config to use to create a concrete player.
     * @param {ObservationCenter} observationCenter The media player's config to use to create a concrete player.
     * @returns AbstractPlayer the new Player object
     *
     */
    static create(config, observationCenter) {

        const playerToCreate = PlayerFactory.getPlayerToCreate(config.playerTypeConfig);

        return new playerToCreate(config, observationCenter);

    }

    /**
     *
     * @desc Returns the player object to create, based on the type passed. Will return undefined if
     * the payer object doesn't exist.
     * @param {string} playerType
     * @returns {string}
     *
     */
    static getPlayerObjectByType(playerType) {

        return playerTypes[playerType];

    }

    static getPlayerTypeFromSource(src, deviceInfo) {

        const webRegExp = /(\.mp4|\.mp3|\.ogg|\.webm)/i;
        const hlsRegExp = /(\.m3u8|format=m3u8)/i;
        const isWeb = webRegExp.test(src);
        const isHls = hlsRegExp.test(src);
        const forceHTML5 = deviceInfo.is.ios; // Force HTML5 player for ios, but not android
        const playerType = (isHls && !forceHTML5 && !isWeb) ? PlayerType.HLSJS : PlayerType.HTML5;

        return playerType;

    }

    /**
     *
     * @desc Gets the type of player to create based on the playerTypeConfig.playerType and src URL.
     * @param {MediaPlayer.PlayerTypeConfig} playerTypeConfig
     * @returns {Object} The object to create for the new player
     *
     */
    static getPlayerToCreate(playerTypeConfig) {

        const mediaItem = _get(playerTypeConfig, 'mediaItem');
        const deviceInfo = _get(playerTypeConfig, 'deviceInfo');
        let playerType = PlayerFactory.getPlayerTypeFromSource(mediaItem.src, deviceInfo);

        _set(playerTypeConfig, 'playerType', playerType);

        return PlayerFactory.getPlayerObjectByType(playerType);

    }

}

export default PlayerFactory;
